/**
 * E-Sign Requests — tracks signing requests sent through buildgravity-dialer's
 * Documenso-backed e-signing API (SPEC esign-relocate-to-dialer-2026-08-07, R3).
 *
 * Super-admin only: this capability is NOT tenant-scoped (a single, internal
 * BuildGravity capability — see buildgravity-dialer/docs/documenso-api-
 * contract.md "Tenant scoping"), and every endpoint is gated server-side on
 * `get_documenso_caller` (an `is_super_admin=true` Firebase user, or a
 * service account — no automated caller exists yet). The nav item is hidden
 * from non-super-admins (Navigation.tsx, mirroring /agency and /voice-agent);
 * this page also short-circuits before issuing any API call so a direct URL
 * visit shows a clear message instead of a raw 403.
 *
 * Built entirely from @metabus/brand primitives (Card/Badge/Button/feedback)
 * per this ring's R-file — this app's own local src/components/ui/* shadcn
 * set is intentionally NOT used here (see output.md "Key decisions").
 */

import { useState } from 'react';
import { FileSignature, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, Badge, Button, Spinner, EmptyState } from '@metabus/brand';
import type { SignatureRequestStatus, SignatureStatus } from '@metabus/esign-client';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useSignatureRequests } from '@/lib/api/hooks/useEsign';

const STATUS_FILTERS: Array<{ value: SignatureStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'signed', label: 'Signed' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'draft', label: 'Draft' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_BADGE_VARIANT: Record<SignatureStatus, 'default' | 'secondary' | 'accent' | 'outline' | 'success' | 'warning' | 'destructive'> = {
  draft: 'outline',
  sent: 'secondary',
  viewed: 'warning',
  signed: 'accent',
  completed: 'success',
  rejected: 'destructive',
  cancelled: 'outline',
  pending: 'warning',
};

function StatusBadge({ status }: { status: SignatureStatus }) {
  return (
    <Badge variant={STATUS_BADGE_VARIANT[status] ?? 'outline'} className="capitalize">
      {status}
    </Badge>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function SigningRequestCard({ request }: { request: SignatureRequestStatus }) {
  const hasSealedDoc = Boolean(request.signed_pdf_path);

  return (
    <Card data-testid={`esign-request-${request.tracking_id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>{request.title}</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Tracking #{request.tracking_id} · Sent by {request.created_by ?? 'unknown'} ·{' '}
              {formatDate(request.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={request.status} />
            {request.live_refresh_failed && (
              <Badge variant="warning">Live refresh failed — showing cached status</Badge>
            )}
            {hasSealedDoc && <Badge variant="success">Sealed document stored</Badge>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Recipients
        </p>
        <ul className="space-y-1">
          {(request.recipients ?? []).map((recipient) => (
            <li
              key={recipient.email}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <span>
                {recipient.name} <span className="text-muted-foreground">({recipient.email})</span>
              </span>
              <span className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs">
                  send: {recipient.send_status ?? 'unknown'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  read: {recipient.read_status ?? 'unknown'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  sign: {recipient.signing_status ?? 'unknown'}
                </Badge>
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function SigningRequestsList() {
  const [statusFilter, setStatusFilter] = useState<SignatureStatus | 'all'>('all');
  const { data, isLoading, isFetching, error, refetch } = useSignatureRequests(
    statusFilter === 'all' ? undefined : { status: statusFilter },
  );

  const items = data?.items ?? [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <FileSignature className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-heading font-bold tracking-tight">E-Sign Requests</h1>
              <p className="text-sm text-muted-foreground">
                Track signing requests sent through the dialer&apos;s e-signing API
              </p>
            </div>
            {!isLoading && (
              <Badge variant="secondary" className="ml-2">
                {data?.count ?? 0} {statusFilter === 'all' ? 'total' : statusFilter}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Status filter */}
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.value}
              size="sm"
              variant={statusFilter === filter.value ? 'default' : 'outline'}
              onClick={() => setStatusFilter(filter.value)}
              aria-pressed={statusFilter === filter.value}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : error ? (
          <Card className="p-8 text-center">
            <p className="mb-2 font-heading text-base font-semibold text-foreground">
              Unable to load signing requests
            </p>
            <p className="mb-4 text-sm text-muted-foreground">{error.message}</p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </Card>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<FileSignature className="h-6 w-6" />}
            title="No signing requests"
            body={
              statusFilter === 'all'
                ? 'Signing requests sent through the dialer will appear here.'
                : `No signing requests with status "${statusFilter}".`
            }
          />
        ) : (
          <div className="space-y-4 max-w-3xl">
            {items.map((request) => (
              <SigningRequestCard key={request.tracking_id} request={request} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ESignPage() {
  const { user } = useAuth();

  if (!user?.is_super_admin) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={<FileSignature className="h-6 w-6" />}
          title="Super admin access required"
          body="E-sign request tracking is a BuildGravity-internal capability, gated to super admins."
        />
      </div>
    );
  }

  return <SigningRequestsList />;
}
